import { useState, useCallback, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  HomeIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ShieldExclamationIcon,
  ScaleIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  BookOpenIcon,
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  EyeIcon as EyeIconSolid,
  MagnifyingGlassIcon as MagnifyingGlassIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  CogIcon as CogIconSolid,
  UserGroupIcon as UserGroupIconSolid,
} from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';
import { useBrand } from '@/components/providers/BrandProvider';
import { useAuth, useAuthActions } from '@/stores/auth';
import { navigationService } from '@/services/navigation.service';
import { isFeatureEnabled, type SectionId } from '@/config/features';
import { isLegalAdminOnly } from '@/utils/access';
import {
  SECURITY_DASHBOARD_PATH,
  SECURITY_ANALYTICS_PATH,
  SECURITY_INCIDENTS_PATH,
  SECURITY_EXECUTIVE_PROTECTION_PATH,
  SECURITY_THREATS_PATH,
  SECURITY_INVESTIGATIONS_PATH,
  SECURITY_SUBJECTS_PATH,
  SECURITY_VENDOR_DIRECTORY_PATH,
} from '@/config/relocatedRoutes';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconSolid?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  badgeKey?: 'incidents' | 'executiveProtection' | 'threats' | 'investigations' | 'subjects';
  /** Pill styling: 'danger' (default) for needs-attention counts, 'neutral' for informational totals. */
  badgeVariant?: 'danger' | 'neutral';
  requiredPermissions?: string[];
  /** Feature-flag module key (see src/config/features.ts). Omit for always-on items. */
  featureModule?: string;
}

interface ModuleSection {
  id: SectionId;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  moduleId: string;
  items: NavigationItem[];
}

// Pinned navigation items (always visible, not in module sections).
// VIS-150: Dashboard and Analytics moved into the Corporate Security section below.
// VIS-211: Global Dashboard pinned to the top, links to the root (Home) landing.
const pinnedTopItems: NavigationItem[] = [
  {
    name: 'Global Dashboard',
    href: '/',
    icon: HomeIcon,
    iconSolid: HomeIconSolid,
  },
];

const pinnedBottomItems: NavigationItem[] = [
  {
    name: 'Settings',
    href: '/settings',
    icon: CogIcon,
    iconSolid: CogIconSolid,
  },
];

// Module-grouped navigation sections
const moduleSections: ModuleSection[] = [
  {
    id: 'security',
    label: 'Corporate Security',
    icon: ShieldExclamationIcon,
    color: 'text-blue-600 dark:text-blue-400',
    moduleId: 'security',
    items: [
      // VIS-150: in-module Dashboard pinned to the top of the group.
      // VIS-211: Analytics moved to the end of the group (see below).
      {
        name: 'Dashboard',
        href: SECURITY_DASHBOARD_PATH,
        icon: HomeIcon,
        iconSolid: HomeIconSolid,
      },
      {
        name: 'Incidents',
        href: SECURITY_INCIDENTS_PATH,
        icon: ExclamationTriangleIcon,
        iconSolid: ExclamationTriangleIconSolid,
        badgeKey: 'incidents',
        requiredPermissions: ['incidents:read'],
        featureModule: 'incidents',
      },
      {
        name: 'Executive Protection',
        href: SECURITY_EXECUTIVE_PROTECTION_PATH,
        icon: ShieldCheckIcon,
        iconSolid: ShieldCheckIconSolid,
        badgeKey: 'executiveProtection',
        requiredPermissions: ['executive_protection:read'],
        featureModule: 'executiveProtection',
      },
      {
        name: 'Threat Intelligence',
        href: SECURITY_THREATS_PATH,
        icon: EyeIcon,
        iconSolid: EyeIconSolid,
        badgeKey: 'threats',
        requiredPermissions: ['threats:read'],
        featureModule: 'threats',
      },
      {
        name: 'Investigations',
        href: SECURITY_INVESTIGATIONS_PATH,
        icon: MagnifyingGlassIcon,
        iconSolid: MagnifyingGlassIconSolid,
        badgeKey: 'investigations',
        requiredPermissions: ['investigations:read'],
        featureModule: 'investigations',
      },
      {
        name: 'Subjects',
        href: SECURITY_SUBJECTS_PATH,
        icon: UserGroupIcon,
        iconSolid: UserGroupIconSolid,
        badgeKey: 'subjects',
        badgeVariant: 'neutral',
        requiredPermissions: ['subjects:read'],
        featureModule: 'subjects',
      },
      {
        name: 'Vendor Directory',
        href: SECURITY_VENDOR_DIRECTORY_PATH,
        icon: BuildingOffice2Icon,
        requiredPermissions: ['security_vendors:read'],
      },
      // VIS-211: Analytics is the last item in the Corporate Security group.
      {
        name: 'Analytics',
        href: SECURITY_ANALYTICS_PATH,
        icon: ChartBarIcon,
        iconSolid: ChartBarIconSolid,
        requiredPermissions: ['analytics:read'],
      },
    ],
  },
  {
    id: 'legal',
    label: 'Legal & Compliance',
    icon: ScaleIcon,
    color: 'text-emerald-600 dark:text-emerald-400',
    moduleId: 'legal',
    items: [
      // VIS-151: L&C Dashboard at the top of the section. Visible to the L&C roles
      // (SUPER_ADMIN/ADMIN have legal_matters:read via inheritance, LEGAL_ADMIN has
      // it directly). No featureModule -> always-on within the section, which is
      // itself hidden for roles without `legal` module access.
      // VIS-211: Analytics moved to the end of the group (see below).
      {
        name: 'Dashboard',
        href: '/legal/dashboard',
        icon: HomeIcon,
        iconSolid: HomeIconSolid,
        requiredPermissions: ['legal_matters:read'],
      },
      {
        name: 'Vendor Management',
        href: '/legal/vendors',
        icon: BuildingOffice2Icon,
        requiredPermissions: ['vendors:read'],
        featureModule: 'vendors',
      },
      {
        name: 'Legal Matters',
        href: '/legal/matters',
        icon: DocumentTextIcon,
        requiredPermissions: ['legal_matters:read'],
        featureModule: 'matters',
      },
      {
        name: 'Governance',
        href: '/legal/governance',
        icon: ClipboardDocumentCheckIcon,
        requiredPermissions: ['governance:read'],
        featureModule: 'governance',
      },
      {
        name: 'Compliance',
        href: '/legal/compliance',
        icon: DocumentCheckIcon,
        requiredPermissions: ['compliance_programs:read'],
        featureModule: 'compliance',
      },
      {
        name: 'Policies',
        href: '/legal/policies',
        icon: BookOpenIcon,
        requiredPermissions: ['policies:read'],
        featureModule: 'policies',
      },
      {
        name: 'Document Tracking',
        href: '/legal/tracking',
        icon: DocumentTextIcon,
        requiredPermissions: ['legal_matters:read'],
        featureModule: 'tracking',
      },
      {
        name: 'HIPAA Incidents',
        href: '/legal/hipaa',
        icon: ShieldExclamationIcon,
        requiredPermissions: ['legal_matters:read'],
        featureModule: 'hipaa',
      },
      {
        name: 'Business Entities',
        href: '/legal/hipaa/business-entities',
        icon: BuildingOffice2Icon,
        requiredPermissions: ['hipaa_incidents:read'],
        featureModule: 'hipaa',
      },
      {
        name: 'Reference Lists',
        href: '/legal/admin/reference-lists',
        icon: ClipboardDocumentCheckIcon,
        requiredPermissions: ['legal_matters:read'],
        featureModule: 'referenceLists',
      },
      {
        name: 'Custom Contracts',
        href: '/legal/custom-contracts',
        icon: DocumentCheckIcon,
        requiredPermissions: ['legal_matters:read'],
        featureModule: 'customContracts',
      },
      // VIS-211: Analytics is the last item in the Legal & Compliance group.
      {
        name: 'Analytics',
        href: '/legal/analytics',
        icon: ChartBarIcon,
        iconSolid: ChartBarIconSolid,
        requiredPermissions: ['legal_matters:read'],
      },
    ],
  },
];

interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  className?: string;
}

export default function Sidebar({ collapsed = false, onCollapsedChange, className }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { checkPermission, checkModuleAccess } = useAuthActions();
  const brand = useBrand();

  // VIS-132: Restrict Legal Admin role to the Legal & Compliance section only.
  // Applies when LEGAL_ADMIN is the user's only privileged role (not also SUPER_ADMIN/ADMIN).
  const roleNames = user?.roles.map(r => r.name) ?? [];
  const legalAdminOnly = isLegalAdminOnly(roleNames);

  const isSectionActive = (section: ModuleSection) =>
    section.items.some(item => location.pathname.startsWith(item.href));

  // The module section the current route belongs to (null on routes outside any
  // section, e.g. Home or Settings).
  const activeSectionId =
    moduleSections.find(section =>
      section.items.some(item => location.pathname.startsWith(item.href))
    )?.id ?? null;

  // Track which module sections are expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (activeSectionId) {
      return new Set([activeSectionId]);
    }
    const firstEnabled = moduleSections.find(section => isFeatureEnabled(section.id));
    return new Set(firstEnabled ? [firstEnabled.id] : []);
  });

  // Keep the expanded section in sync with navigation: entering a section's route
  // expands it and collapses the others (accordion). Only fires when the active
  // section actually changes, so manual toggles within a section are preserved.
  useEffect(() => {
    if (activeSectionId) {
      setExpandedSections(new Set([activeSectionId]));
    }
  }, [activeSectionId]);

  // Fetch navigation stats
  const { data: navStats } = useQuery({
    queryKey: ['navigation-stats'],
    queryFn: () => navigationService.getNavigationStats(),
    refetchInterval: 300000,
    staleTime: 240000,
  });

  const toggleCollapsed = () => {
    onCollapsedChange?.(!collapsed);
  };

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const sidebarVariants = {
    expanded: {
      width: '16rem',
      transition: { duration: 0.3, ease: 'easeInOut' },
    },
    collapsed: {
      width: '5rem',
      transition: { duration: 0.3, ease: 'easeInOut' },
    },
  };

  const contentVariants = {
    expanded: {
      opacity: 1,
      transition: { delay: 0.1, duration: 0.2 },
    },
    collapsed: {
      opacity: 0,
      transition: { duration: 0.1 },
    },
  };

  // Filter items based on permissions
  const hasItemPermission = (item: NavigationItem) => {
    if (!item.requiredPermissions?.length) {return true;}
    return item.requiredPermissions.some(permission => checkPermission(permission));
  };

  // Filter module sections based on the build's feature flags first (applies to
  // everyone), then module access.
  const accessibleSections = moduleSections.filter(section => {
    if (!isFeatureEnabled(section.id)) {return false;}
    // VIS-132: Legal Admin users only see the Legal & Compliance section.
    if (legalAdminOnly) {return section.id === 'legal';}
    if (user?.roles.some(r => r.name === 'SUPER_ADMIN')) {return true;}
    return checkModuleAccess(section.moduleId);
  });

  // VIS-132: Hide pinned items (Global Dashboard, Settings) for Legal Admin only users,
  // consistent with the L&C-only restriction on other global surfaces.
  // Permission-filter here so the divider gates on what actually renders (avoids an
  // orphan divider if a future pinned item is permission-gated out).
  const visiblePinnedTopItems = (legalAdminOnly ? [] : pinnedTopItems).filter(hasItemPermission);
  const visiblePinnedBottomItems = (legalAdminOnly ? [] : pinnedBottomItems).filter(hasItemPermission);

  const renderNavItem = (item: NavigationItem) => {
    // VIS-211: `/` (Global Dashboard) is a prefix of every path, so an exact match
    // is required — otherwise it would render active on every route.
    const isRoot = item.href === '/';
    const isActive = isRoot
      ? location.pathname === '/'
      : location.pathname.startsWith(item.href);
    const Icon = isActive && item.iconSolid ? item.iconSolid : item.icon;

    return (
      <NavLink
        key={item.name}
        to={item.href}
        end={isRoot}
        className={({ isActive }) =>
          cn(
            'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500',
            isActive
              ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-100'
              : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-secondary-100 dark:hover:bg-secondary-800'
          )
        }
        aria-label={`Navigate to ${item.name}`}
      >
        <Icon className={cn('w-5 h-5 flex-shrink-0', collapsed ? 'mx-auto' : 'mr-3')} />

        <AnimatePresence mode="wait">
          {!collapsed && (
            <motion.div
              variants={contentVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              className="flex items-center justify-between flex-1 min-w-0"
            >
              <span className="truncate">{item.name}</span>
              {item.badgeKey && navStats && navStats[item.badgeKey] > 0 && (
                <span
                  className={cn(
                    'ml-2 px-2 py-0.5 text-xs font-medium rounded-full',
                    item.badgeVariant === 'neutral'
                      ? 'bg-secondary-100 text-secondary-700 dark:bg-secondary-700/50 dark:text-secondary-300'
                      : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                  )}
                >
                  {navStats[item.badgeKey]}
                </span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tooltip for collapsed state */}
        {collapsed && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-secondary-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 dark:bg-white dark:text-secondary-900">
            {item.name}
            {item.badgeKey && navStats && navStats[item.badgeKey] > 0 && (
              <span
                className={cn(
                  'ml-1 px-1.5 py-0.5 text-xs text-white rounded-full',
                  item.badgeVariant === 'neutral' ? 'bg-secondary-600' : 'bg-danger-600'
                )}
              >
                {navStats[item.badgeKey]}
              </span>
            )}
          </div>
        )}
      </NavLink>
    );
  };

  return (
    <motion.div
      variants={sidebarVariants}
      animate={collapsed ? 'collapsed' : 'expanded'}
      className={cn(
        'flex flex-col bg-white dark:bg-secondary-900 border-r border-secondary-200 dark:border-secondary-700 h-full',
        className
      )}
    >
      {/* Header */}
      <div className="relative flex items-center justify-center p-4 border-b border-secondary-200 dark:border-secondary-700">
        <AnimatePresence mode="wait">
          {!collapsed && (
            <motion.div
              variants={contentVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              className="flex flex-col items-center space-y-0"
            >
              <div className="w-40 h-20 flex items-center justify-center">
                <img
                  src={`/${brand.assets.logo}`}
                  alt={`${brand.name} Logo`}
                  className="w-full h-full object-contain drop-shadow-md"
                />
              </div>
              <div className="text-center">
                <h1 className="text-sm font-semibold text-secondary-900 dark:text-white">
                  {brand.name}
                </h1>
                {brand.appSubtitle && (
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    {brand.appSubtitle}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={toggleCollapsed}
          className="absolute right-4 p-1.5 rounded-lg text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-secondary-200 dark:hover:bg-secondary-800 transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRightIcon className="w-4 h-4" />
          ) : (
            <ChevronLeftIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Pinned top items (VIS-211: Global Dashboard) */}
        {visiblePinnedTopItems.map(renderNavItem)}
        {visiblePinnedTopItems.length > 0 && (
          <div className="my-3 border-t border-secondary-200 dark:border-secondary-700" />
        )}

        {/* Module sections */}
        {accessibleSections.map((section) => {
          const filteredItems = section.items.filter(
            item =>
              hasItemPermission(item) &&
              (!item.featureModule ||
                isFeatureEnabled(section.id, item.featureModule))
          );
          if (filteredItems.length === 0) {return null;}

          const isExpanded = expandedSections.has(section.id);
          const sectionActive = isSectionActive(section);
          const SectionIcon = section.icon;

          return (
            <div key={section.id} className="mt-3">
              {/* Section header */}
              {!collapsed ? (
                <button
                  onClick={() => toggleSection(section.id)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors',
                    sectionActive
                      ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/10'
                      : 'text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800/50'
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <SectionIcon className={cn('w-4 h-4', section.color)} />
                    <span>{section.label}</span>
                  </div>
                  <ChevronDownIcon
                    className={cn(
                      'w-3.5 h-3.5 transition-transform duration-200',
                      isExpanded ? 'rotate-0' : '-rotate-90'
                    )}
                  />
                </button>
              ) : (
                <div className="flex justify-center py-2">
                  <div
                    className={cn('w-6 h-0.5 rounded-full', sectionActive ? 'bg-primary-500' : 'bg-secondary-300 dark:bg-secondary-600')}
                  />
                </div>
              )}

              {/* Section items */}
              <AnimatePresence initial={false}>
                {(isExpanded || collapsed) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className={cn('space-y-0.5', !collapsed && 'ml-2 mt-1')}>
                      {filteredItems.map(renderNavItem)}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* Separator before bottom items */}
        {visiblePinnedBottomItems.length > 0 && (
          <div className="my-3 border-t border-secondary-200 dark:border-secondary-700" />
        )}

        {/* Pinned bottom items */}
        {visiblePinnedBottomItems.map(renderNavItem)}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-secondary-200 dark:border-secondary-700">
        <AnimatePresence mode="wait">
          {!collapsed && (
            <motion.div
              variants={contentVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              className="text-xs text-secondary-500 dark:text-secondary-400 text-center"
            >
              Version 1.0.0
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
